import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";

dayjs.extend(utc);
dayjs.extend(timezone);

// Enable AI QC on staging only on Thursdays and Fridays,
// between 2pm and 12:30am PHT
export const shouldEnableAIQualityCheck = () => {
  const now = dayjs().tz("Asia/Manila");
  const dayOfWeek = now.day(); // 0 = Sunday, 1 = Monday, ..., 4 = Thursday, 5 = Friday
  const hour = now.hour();
  const minute = now.minute();
  const currentTime = hour * 60 + minute; // Convert to minutes for easier comparison
  const startTime = 14 * 60; // 2pm = 14:00 = 840 minutes
  const endTime = 0 * 60 + 30; // 12:30am = 00:30 = 30 minutes

  // Thursday window: 2pm Thursday to 12:30am Friday
  // Friday window: 2pm Friday to 12:30am Saturday

  if (dayOfWeek === 4) {
    // Thursday: check if time >= 2pm
    return currentTime >= startTime;
  } else if (dayOfWeek === 5) {
    // Friday: check if time < 12:30am (part of Thursday's window) OR time >= 2pm (Friday's window)
    return currentTime < endTime || currentTime >= startTime;
  } else if (dayOfWeek === 6) {
    // Saturday early morning: check if it's before 12:30am (still part of Friday's window)
    return currentTime < endTime;
  }

  return false;
};

const STORAGE_KEY = "aiqc:resolved";

export const normalize = (value) =>
  (value ?? "").toString().trim().split(" ").join("_").toLowerCase();

const readStore = () => {
  if (typeof window === "undefined") {
    return {};
  }

  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) {
      return {};
    }
    const parsed = JSON.parse(raw);
    return typeof parsed === "object" && parsed !== null ? parsed : {};
  } catch (error) {
    console.warn("AI QC: failed to read resolved issues from storage", error);
    return {};
  }
};

const writeStore = (store) => {
  if (typeof window === "undefined") {
    return;
  }

  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(store));
  } catch (error) {
    console.warn("AI QC: failed to persist resolved issues", error);
  }
};

export const loadResolvedSet = (workflowId) => {
  if (!workflowId) {
    return new Set();
  }

  const store = readStore();
  const entries = store[workflowId];
  return Array.isArray(entries) ? new Set(entries) : new Set();
};

export const saveResolvedSet = (workflowId, set) => {
  if (!workflowId) {
    return;
  }

  const store = readStore();
  store[workflowId] = Array.from(set);
  writeStore(store);
};

export const makeIssueKey = ({ category, entity, startTime }) =>
  `${normalize(category)}:${normalize(entity)}:${startTime}`;

export const addResolved = (workflowId, key) => {
  if (!workflowId) {
    return;
  }

  const existing = loadResolvedSet(workflowId);
  if (!existing.has(key)) {
    existing.add(key);
    saveResolvedSet(workflowId, existing);
  }
};

export const removeResolvedSet = (workflowId) => {
  if (!workflowId) {
    return;
  }

  const store = readStore();
  delete store[workflowId];
  writeStore(store);
};

export const reasons = [
  "Misheard Audio",
  "Wrong Spelling",
  "Wrong Name / Identity",
  "Context Misunderstood",
  "Other",
];

export const reasonBtnSx = (active, isDarkMode) => ({
  borderRadius: 2,
  padding: "6px 12px",
  fontSize: "14px",
  minWidth: 0,
  border: `1px solid ${
    active ? "#2563EB" : isDarkMode ? "#52525B" : "#D1D1D1"
  }`,
  color: active ? "#2563EB" : isDarkMode ? "#FFF" : "#111827",
  bgcolor: active ? (isDarkMode ? "#0A1E46" : "#EEF6FF") : "transparent",

  "&:hover": {
    color: active ? "#2563EB" : isDarkMode ? "#FFF" : "#1C1C1C",
    bgcolor: active
      ? isDarkMode
        ? "#0A1E46"
        : "#EEF6FF"
      : isDarkMode
      ? "#31343B"
      : "#EDEDED",
    borderColor: active ? "#2563EB" : isDarkMode ? "#52525B" : "#D1D1D1",
  },
});
